<template>
  <div
    class="modal fade bd-example-modal-lg"
    id="reportSummarModal"
    tabindex="-1"
    role="dialog"
    ref="modalRef"
    aria-labelledby="exampleModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex">
            <h4 class="col-md-12 mt-4">Transfer Plan Summary Report</h4>
            <button
              type="button"
              class="close text-right close-button"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <hr />
          <div class="report" v-html="report"></div>

          <div class="no-results text-center" v-if="loading">
            <div class="loader"></div>
          </div>
        </div>
        <div class="text-center modal-footer">
          <button
            type="button"
            class="btn btn-secondary btn-close"
            data-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "ReportSummaryModal",
  props: {
    report: {
      type: [Object, String],
      required: false,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
});
</script>
<style>
/* --- Modal polish --- */
.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  overflow: hidden;
}
.modal-body { padding: 1.25rem 1.25rem 1rem; }
.modal-footer { border-top: 1px solid #e7eaf0; }
hr { border-top: 1px solid #e7eaf0; margin: 0.75rem 0 1rem; }

h4 {
  font-weight: 600;
  font-size: 1.2rem;
  letter-spacing: 0.2px;
  margin: 0;
}

/* Close buttons */
.close-button span, .btn-close {
  font-size: 1.25rem;
  color: #475569;
  transition: color .15s ease;
}
.close-button:hover span, .btn-close:hover { color: #0f172a; }

/* --- Loader --- */
.loader {
  width: 40px; height: 40px;
  border-radius: 50%;
  border: 4px solid #eef2f7;
  border-top-color: #0d6efd;
  margin: 16px auto;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* --- Report base --- */
.report {
  font-size: 14px;
  color: #1f2937;
  line-height: 1.6;
  overflow-x: auto;               
  -webkit-overflow-scrolling: touch;
}

/* --- Table makeover (applies to any table inside .report) --- */
.report table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 720px;                
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
  font-variant-numeric: tabular-nums;
}

.report thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  font-weight: 600;
  color: #111827;
  background: linear-gradient(#f8fafc, #f3f6fb);
  border-bottom: 1px solid #e1e6ef;
  padding: 12px 14px;
  white-space: nowrap;
}

.report tbody td {
  padding: 12px 14px;
  color: #334155;
  vertical-align: top;
  border-bottom: 1px solid #eef2f7;
}

.report tbody tr:nth-child(even) td { background: #fbfdff; }
.report tbody tr:hover td { background: #f5f9ff; }

.report td:first-child, .report th:first-child { padding-left: 16px; }
.report tbody td:first-child { font-weight: 500; color: #111827; }

/* Remove bottom border on last row and round corners */
.report table tr:last-child td { border-bottom: 0; }
.report thead th:first-child { border-top-left-radius: 12px; }
.report thead th:last-child  { border-top-right-radius: 12px; }
.report tbody tr:last-child td:first-child { border-bottom-left-radius: 12px; }
.report tbody tr:last-child td:last-child  { border-bottom-right-radius: 12px; }

/* Helpers for numbers and status colors (use if present in HTML) */
.report td.num, .report th.num { text-align: right; }
.report .positive { color: #0f9d58; font-weight: 600; }
.report .negative { color: #e11d48; font-weight: 600; }

/* Compact mode (apply class="compact" to the table if needed) */
.report table.compact th,
.report table.compact td { padding: 8px 10px; }

/* Small screens */
@media (max-width: 768px) {
  .report table { min-width: 600px; }
}

/* Existing utility classes */
.error-message { color: red; }
.text-center { text-align: center !important; }
.pre-wrap { white-space: pre-wrap; }
/* --- Headings inside report --- */
.report h1, .report h2, .report h3, .report h4 {
  font-weight: 600;
  color: #0f172a;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  letter-spacing: -0.2px;
}
.report h2 {
  font-size: 1.2rem;
  border-left: 4px solid #0d6efd;
  padding-left: 8px;
}

/* --- Paragraphs and text --- */
.report p {
  margin: 0.5rem 0 1rem;
  color: #374151;
  line-height: 1.65;
}

/* --- Credit summary highlight box --- */
.report .summary-box {
  border: 1px solid #e0e7ff;
  border-left: 6px solid #0d6efd;
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 1rem 0 1.5rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
}
.report .summary-box h3 {
  margin-top: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
}
.report .summary-box p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
}

/* --- Callout for transfers/losses --- */
.report .transfer-good {
  color: #15803d;
  font-weight: 600;
}
.report .transfer-bad {
  color: #dc2626;
  font-weight: 600;
}

/* ---  Subtle animation on hover --- */
.report tbody tr {
  transition: background-color 0.15s ease;
}
.report tbody tr:hover td {
  background: #f1f5ff;
}

/* --- Spacing for modal content --- */
.modal-body {
  background: #fafafa;
}
.modal-footer {
  background: #f9fafb;
}
</style>